import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, MapPin, Star, UserPlus, X } from "lucide-react";
import api from "@/lib/api";

const ICONS = { live: Sparkles, new: UserPlus, tour: MapPin, review: Star };
const COLORS = {
  live: "text-velvet-rose",
  new: "text-velvet-gold",
  tour: "text-white",
  review: "text-velvet-gold",
};

/**
 * Bottom-of-page infinite-scrolling activity ticker.
 *
 * Uses a CSS keyframe animation (defined in index.css as `.vx-ticker-anim`)
 * for buttery-smooth marquee — more reliable than a JS RAF loop, and
 * automatically pauses via `animation-play-state` when the tab is hidden
 * (browsers throttle animation frames on hidden tabs).
 */
export default function LiveTicker() {
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("vixera:ticker-dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api.get("/live-feed?limit=25").then((r) => {
        if (!cancelled) setEvents(r.data);
      }).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Hide on admin/auth routes
  const hidden = /^\/(admin|login|register|choose-username|auth\/)/.test(location.pathname);

  // Duplicate the list so the -50% translate loops seamlessly.
  const loop = useMemo(() => [...events, ...events], [events]);

  // Animation duration scales with content length — keeps speed roughly
  // constant regardless of event count.
  const durationSec = Math.max(30, events.length * 5);

  if (hidden || dismissed || events.length === 0) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("vixera:ticker-dismissed", "1"); } catch {}
  };

  return (
    <motion.div
      initial={{ y: 60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 1.5 }}
      className="pointer-events-auto fixed bottom-0 left-0 right-0 z-40 border-t border-velvet-gold/20 bg-black/85 backdrop-blur-lg"
      data-testid="live-ticker"
    >
      <div className="relative flex items-center overflow-hidden">
        <div className="hidden shrink-0 items-center gap-2 border-r border-white/10 px-5 py-3 md:flex">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-velvet-rose opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-velvet-rose" />
          </span>
          <span className="text-[10px] uppercase tracking-luxe text-velvet-rose">Live on Vixera</span>
        </div>

        {/* Marquee viewport — flex-1 + min-w-0 so it can actually shrink */}
        <div
          className="relative min-w-0 flex-1 overflow-hidden py-3"
          style={{ maskImage: "linear-gradient(90deg, transparent, black 4%, black 96%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, black 4%, black 96%, transparent)" }}
        >
          {/* Inline-flex track — width = sum of children widths, animated -50% */}
          <div
            className="vx-ticker-anim flex w-max items-center whitespace-nowrap"
            style={{ animationDuration: `${durationSec}s` }}
          >
            {loop.map((e, i) => {
              const Icon = ICONS[e.type] || Sparkles;
              const color = COLORS[e.type] || "text-white";
              return (
                <Link
                  key={`${e.slug}-${i}`}
                  to={e.slug ? `/companion/${e.slug}` : "/directory"}
                  data-testid={`ticker-event-${i}`}
                  className="group mx-8 inline-flex items-center gap-3 text-sm text-white/80 transition-colors hover:text-white"
                >
                  <Icon size={12} className={`shrink-0 ${color}`} />
                  <span className="font-light">{e.text}</span>
                  {e.when && (
                    <span className="text-[10px] uppercase tracking-luxe text-white/30">· {e.when}</span>
                  )}
                  <span className="text-velvet-gold/40 group-hover:text-velvet-gold">→</span>
                </Link>
              );
            })}
          </div>
        </div>

        <button
          onClick={dismiss}
          aria-label="Dismiss live ticker"
          data-testid="ticker-dismiss"
          className="shrink-0 border-l border-white/10 px-3 py-3 text-white/40 transition-colors hover:text-white"
        >
          <X size={14} />
        </button>
      </div>
    </motion.div>
  );
}
